/**
 * Rendering engine - render, batch, and progressive rendering utilities
 */

import type { CanvasElement, DirtyRegion } from './types'
import { getOrCreatePath, intersectsViewport } from './pathCache'

type RenderContext = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D

/**
 * Render a single element to the specified context
 */
export function renderElementToContext(
  element: CanvasElement,
  ctx: RenderContext,
  pathCache: Map<string, Path2D>
): void {
  const path = getOrCreatePath(pathCache, element)

  ctx.save()
  ctx.translate(element.position.x, element.position.y)
  ctx.globalAlpha = element.opacity

  if (element.fill && element.fill !== 'none') {
    ctx.fillStyle = element.fill
    ctx.fill(path)
  }

  if (element.stroke && element.stroke !== 'none') {
    ctx.strokeStyle = element.stroke
    ctx.lineWidth = element.strokeWidth
    ctx.stroke(path)
  }

  ctx.restore()
}

/**
 * Generate batch key for grouping similar elements
 */
export function getBatchKey(element: CanvasElement): string {
  return `${element.fill}_${element.stroke}_${element.strokeWidth}_${element.opacity}`
}

/**
 * Group elements into batches by style
 */
export function groupElementsIntoBatches(
  elements: CanvasElement[]
): Map<string, CanvasElement[]> {
  const batches = new Map<string, CanvasElement[]>()

  for (const element of elements) {
    const key = getBatchKey(element)
    const batch = batches.get(key) || []
    batch.push(element)
    batches.set(key, batch)
  }

  return batches
}

/**
 * Render a batch of elements with the same style
 */
export function renderBatch(
  batch: CanvasElement[],
  ctx: RenderContext,
  pathCache: Map<string, Path2D>
): void {
  if (batch.length === 0) return

  const firstElement = batch[0]

  ctx.save()
  ctx.globalAlpha = firstElement.opacity

  const needsFill = firstElement.fill && firstElement.fill !== 'none'
  if (needsFill) {
    ctx.fillStyle = firstElement.fill
  }

  const needsStroke = firstElement.stroke && firstElement.stroke !== 'none'
  if (needsStroke) {
    ctx.strokeStyle = firstElement.stroke
    ctx.lineWidth = firstElement.strokeWidth
  }

  for (const element of batch) {
    const path = getOrCreatePath(pathCache, element)

    ctx.save()
    ctx.translate(element.position.x, element.position.y)

    if (needsFill) {
      ctx.fill(path)
    }
    if (needsStroke) {
      ctx.stroke(path)
    }

    ctx.restore()
  }

  ctx.restore()
}

/**
 * Filter elements that intersect with any dirty region
 */
export function filterElementsByDirtyRegions(
  elements: CanvasElement[],
  dirtyRegions: DirtyRegion[]
): CanvasElement[] {
  if (dirtyRegions.length === 0) {
    return elements
  }

  return elements.filter(element =>
    dirtyRegions.some(region =>
      intersectsViewport(element.bounds, region)
    )
  )
}

/**
 * Check if a point is inside an element using Path2D hit testing
 */
export function isPointInElement(
  x: number,
  y: number,
  element: CanvasElement,
  ctx: CanvasRenderingContext2D,
  pathCache: Map<string, Path2D>
): boolean {
  const localX = x - element.position.x
  const localY = y - element.position.y

  if (localX < 0 || localY < 0 ||
      localX > element.bounds.width ||
      localY > element.bounds.height) {
    return false
  }

  const path = getOrCreatePath(pathCache, element)
  return ctx.isPointInPath(path, localX, localY)
}
